import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Drawer,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  RocketLaunch,
  AutoAwesome,
  Insights,
  Help,
  Tune,
  AdminPanelSettings,
  BugReport,
  Close
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCurrentUser } from '../../hooks/useApi';

interface Article {
  id: string;
  title: string;
  path: string;
  description: string;
  tags: string[];
  order: number;
}

interface Category {
  id: string;
  title: string;
  description: string;
  icon: string;
  requiresAuth: boolean;
  requiresPermission: string | null;
  order: number;
  articles: Article[];
}

interface DocsSidebarProps {
  categories: Category[];
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

// Map icon names to icon components
const iconMap: Record<string, React.ElementType> = {
  RocketLaunch,
  AutoAwesome,
  Insights,
  Help,
  Tune,
  AdminPanelSettings,
  BugReport
};

export const DocsSidebar: React.FC<DocsSidebarProps> = ({
  categories,
  mobileOpen = false,
  onMobileClose
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { data: user } = useCurrentUser();

  // Track expanded categories - persist in localStorage
  const [expandedCategories, setExpandedCategories] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('docs-expanded-categories');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Auto-expand the category containing the current article
  useEffect(() => {
    // Extract categoryId from the current path
    const pathParts = location.pathname.split('/');
    const docsIndex = pathParts.indexOf('docs');

    if (docsIndex !== -1) {
      // Check if this is a privileged path: /docs/privileged/:categoryId/:articleId
      // or public path: /docs/:categoryId/:articleId
      let categoryId: string | undefined;

      if (pathParts[docsIndex + 1] === 'privileged') {
        categoryId = pathParts[docsIndex + 2];
      } else if (pathParts.length > docsIndex + 1) {
        categoryId = pathParts[docsIndex + 1];
      }

      // If we found a category, ensure it's expanded
      if (categoryId) {
        setExpandedCategories(prev => {
          // Only update if it's not already in the array
          if (!prev.includes(categoryId!)) {
            const updated = [...prev, categoryId!];
            localStorage.setItem('docs-expanded-categories', JSON.stringify(updated));
            return updated;
          }
          return prev;
        });
      }
    }
  }, [location.pathname]); // Re-run when location changes

  // Filter categories based on user permissions
  const visibleCategories = categories.filter(category => {
    // If category requires permission, check if user has it
    if (category.requiresPermission) {
      return user?.permissions?.includes(category.requiresPermission);
    }
    // If category requires auth but no specific permission, check if user is authenticated
    if (category.requiresAuth) {
      return !!user;
    }
    // Public category - always visible
    return true;
  });

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const updated = prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId];
      localStorage.setItem('docs-expanded-categories', JSON.stringify(updated));
      return updated;
    });
  };

  const handleArticleClick = (category: Category, article: Article) => {
    const basePath = category.requiresPermission ? '/docs/privileged' : '/docs';
    navigate(`${basePath}/${category.id}/${article.id}`);
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  const isArticleActive = (category: Category, article: Article) => {
    const basePath = category.requiresPermission ? '/docs/privileged' : '/docs';
    return location.pathname === `${basePath}/${category.id}/${article.id}`;
  };

  const sidebarContent = (
    <Box sx={{ width: { xs: 280, md: 320 }, height: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography variant="h6" component="div">
          Documentation
        </Typography>
        {isMobile && onMobileClose && (
          <IconButton onClick={onMobileClose} size="small">
            <Close />
          </IconButton>
        )}
      </Box>

      {/* Categories and Articles */}
      <List sx={{ px: 1, py: 2 }}>
        {visibleCategories.map(category => {
          const IconComponent = iconMap[category.icon] || Help;
          const isExpanded = expandedCategories.includes(category.id);

          return (
            <React.Fragment key={category.id}>
              {/* Category Header */}
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => toggleCategory(category.id)}
                  sx={{
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <IconComponent sx={{ color: 'primary.main' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={category.title}
                    primaryTypographyProps={{
                      variant: 'subtitle2',
                      fontWeight: 600
                    }}
                  />
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>

              {/* Category Articles */}
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {category.articles.map(article => (
                    <ListItem key={article.id} disablePadding sx={{ pl: 2 }}>
                      <ListItemButton
                        onClick={() => handleArticleClick(category, article)}
                        selected={isArticleActive(category, article)}
                        sx={{
                          borderRadius: 1,
                          py: 0.75,
                          pl: 3,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.light',
                            color: 'primary.main',
                            fontWeight: 600,
                            '&:hover': {
                              backgroundColor: 'primary.light'
                            }
                          }
                        }}
                      >
                        <ListItemText
                          primary={article.title}
                          primaryTypographyProps={{
                            variant: 'body2',
                            sx: {
                              fontSize: '0.875rem',
                              fontWeight: isArticleActive(category, article) ? 600 : 400
                            }
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );

  // Mobile drawer
  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true // Better open performance on mobile
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280
          }
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  // Desktop permanent sidebar
  return (
    <Box
      sx={{
        width: 320,
        flexShrink: 0,
        borderRight: 1,
        borderColor: 'divider',
        height: '100%',
        overflow: 'auto'
      }}
    >
      {sidebarContent}
    </Box>
  );
};
